import { Alert, Box, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import type { IngestJobUi } from "../../services/ragDocumentApi";
import { formatConfirmEnqueueFailureLine, friendlyIngestionJobError } from "../../utils/documentManagementUserMessages";

export type ConfirmEnqueueFailureUi = {
  fileName: string;
  error: string;
};

export type DocumentIngestionProgressProps = {
  jobs: IngestJobUi[];
  confirmFailures?: ConfirmEnqueueFailureUi[];
  /** Override section title (e.g. metadata detection). */
  title?: string;
  waitingLabel?: string;
};

export function DocumentIngestionProgress({
  jobs,
  confirmFailures = [],
  title,
  waitingLabel,
}: DocumentIngestionProgressProps) {
  const { t } = useTranslation();
  const sectionTitle =
    title ??
    t("opva.dmIngestionProgressTitle", {
      defaultValue: "Processing documents",
    });
  const waitingText =
    waitingLabel ??
    t("opva.dmIngestWaiting", {
      defaultValue: "Waiting for worker…",
    });

  if (jobs.length === 0 && confirmFailures.length === 0) {
    return null;
  }

  return (
    <Box component="section" className="dm-ingest-panel">
      <Typography variant="h6" fontWeight={600} sx={{ mb: 1.5 }}>
        {sectionTitle}
        {jobs.length > 0
          ? t("opva.dmIngestionProgressCount", {
              defaultValue: " ({{count}})",
              count: jobs.length,
            })
          : null}
      </Typography>
      {confirmFailures.length > 0 ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight={600} gutterBottom>
            {t("opva.dmConfirmEnqueueFailuresTitle", {
              defaultValue: "Could not start processing for:",
            })}
          </Typography>
          {confirmFailures.map((f) => (
            <Typography key={f.fileName} variant="caption" display="block">
              {formatConfirmEnqueueFailureLine(f.fileName, f.error, t)}
            </Typography>
          ))}
        </Alert>
      ) : null}
      <Stack spacing={1.5}>
        {jobs.map((j) => (
          <Box key={j.jobId} className="dm-ingest-job">
            <Typography
              variant="body2"
              fontWeight={600}
              noWrap
              title={j.fileName}
              sx={{ mb: 0.5 }}
            >
              {j.fileName}
            </Typography>
            {j.last ? (
              <>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 0.5,
                  }}
                >
                  <Typography variant="caption">
                    {t("opva.dmIngestPhase", { defaultValue: "Phase" })}:{" "}
                    <Typography
                      component="span"
                      variant="caption"
                      color={
                        j.last.status === "failed"
                          ? "error"
                          : j.last.status === "completed"
                            ? "success.main"
                            : "primary"
                      }
                    >
                      {j.last.phase}
                    </Typography>
                  </Typography>
                  <Typography variant="caption">
                    {j.last.progress_pct ?? 0}%
                  </Typography>
                </Box>
                <Box className="dm-ingest-progress-track">
                  <Box
                    className={`dm-ingest-progress-fill dm-ingest-progress-fill--${
                      j.last.status === "failed"
                        ? "failed"
                        : j.last.status === "completed"
                          ? "done"
                          : "running"
                    }`}
                    sx={{
                      width: `${Math.min(100, Number(j.last.progress_pct) || 0)}%`,
                    }}
                  />
                </Box>
                {j.last.error_message ? (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ mt: 0.5, display: "block" }}
                  >
                    {friendlyIngestionJobError(j.last.error_message, t)}
                  </Typography>
                ) : null}
              </>
            ) : (
              <Typography variant="caption" color="text.secondary">
                {waitingText}
              </Typography>
            )}
          </Box>
        ))}
      </Stack>
    </Box>
  );
}
